/**
 * Browser-wide shared session — every tab and window of this browser profile
 * shares ONE login. Logging in anywhere logs every tab in; logging out anywhere
 * logs every tab out.
 *
 * WHERE THE SESSION LIVES. Still `sessionStorage["persist:root"]` (+ the CSRF
 * keys) in each tab, exactly as before — so the session still ends when the
 * whole browser closes. What is new is that tabs keep each other in sync:
 *
 *   1. Bootstrap. A tab that loads with no session of its own asks the open tabs
 *      for theirs (SESSION_REQUEST) before redux-persist rehydrates; the first
 *      SESSION_REPLY wins and is copied into this tab's sessionStorage.
 *   2. Live sync. When this tab's token or user changes from a LOCAL action
 *      (login, token renewal) it broadcasts SESSION; when the token goes away
 *      (RESET_APP) it broadcasts LOGOUT. Watching the store — instead of adding a
 *      broadcast to each sign-in function — means no login path can forget it.
 *      A tab in the background applies a remote logout's teardown at once (token
 *      gone, timers/streams stopped) but holds the page reload — and a remote
 *      login entirely — until it is shown, so N open tabs don't all reload at
 *      once. A token renewal is just a value swap and is applied at once.
 *   3. Renewal leader. Only one tab runs the 5-hour token-renewal interval
 *      (navigator.locks); the renewed token reaches the other tabs through (2).
 *
 * DETACHED TABS take no part in any of this — they never send their session and
 * never accept one: the /try-haire demo (throwaway session), an "Open as company"
 * tab (an admin acting as a customer — must stay isolated), and the standalone
 * pages (admin portal, company preview), which have their own login or none.
 *
 * Transport is BroadcastChannel, with `storage` events as the fallback for older
 * Safari. The fallback writes the message to localStorage for an instant and
 * removes it, so nothing outlives the last tab.
 *
 * Rollback: VITE_SHARED_SESSION=false makes all of this inert (each tab keeps a
 * strictly separate session; a new tab starts logged out).
 */
import {
  isInterviewTabLockHeld,
  onInterviewTabLockChange,
} from "./interviewTabLock";
import { isAdminPortalPath } from "../pages/HaireAdmin/adminPortalPath";

const CHANNEL_NAME = "haire-auth";
const FALLBACK_MSG_KEY = "haire-auth:msg";
const PRESENCE_LOCK = "haire-auth:presence";
const RENEWAL_LOCK = "haire-auth:renewal";
const DETACHED_KEY = "haire-auth:detached";
const OPEN_AS_SESSION_KEY = "haire-open-as-session"; // see utils/openAsCompany.js
const ROOT_KEY = "persist:root";
const CSRF_KEYS = ["csrf_token", "csrftoken"];

/** How long a new tab waits for a reply when another tab is known to hold a session. */
const BOOTSTRAP_TIMEOUT_MS = 1500;
/** …and when that cannot be known (no navigator.locks). Kept short: it delays first paint. */
const BOOTSTRAP_BLIND_TIMEOUT_MS = 300;
/** Lets a login's follow-up dispatches (setUser after setAuthToken) land before we snapshot. */
const SESSION_BROADCAST_DEBOUNCE_MS = 300;

/** Pre-shared-session keys that held a full session (token included) in localStorage. */
const LEGACY_SEED_KEYS = [
  "persist:seed:main",
  "persist:seed:client",
  "persist:seed:applicant",
  "persist:link-seed",
  "persist:link-seed-ts",
];

export interface SessionSnapshot {
  root: string;
  csrf: string | null;
}

type AuthMessage =
  | { type: "SESSION_REQUEST"; from: string; requestId: string }
  | { type: "SESSION_REPLY"; from: string; requestId: string; snapshot: SessionSnapshot }
  | { type: "SESSION"; from: string; token: string; userId: string | null; snapshot: SessionSnapshot }
  | { type: "LOGOUT"; from: string };

export const isSharedSessionEnabled = (): boolean =>
  import.meta.env?.VITE_SHARED_SESSION !== "false";

const randomId = (): string => {
  try {
    return window.crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/** This page load's identity — lets a tab ignore its own messages on the storage fallback. */
const TAB_ID = randomId();

/**
 * True only for a tab the user cannot see (background tab, minimised window).
 * Compared to "hidden" rather than read from document.hidden so an unknown state
 * (e.g. "prerender") counts as visible and applies changes right away.
 */
const isTabHidden = (): boolean => {
  try {
    return document.visibilityState === "hidden";
  } catch {
    return false;
  }
};

// ── Detached tabs ────────────────────────────────────────────────────────────

const isDetachedPath = (path: string): boolean =>
  path.startsWith("/try-haire") ||
  path === "/open-as" ||
  path === "/open-as/" ||
  /^\/company-preview\//.test(path) ||
  isAdminPortalPath(path);

const safeSession = {
  get: (k: string): string | null => {
    try {
      return window.sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      window.sessionStorage.setItem(k, v);
    } catch {
      /* storage unavailable (privacy mode / quota) */
    }
  },
  remove: (k: string) => {
    try {
      window.sessionStorage.removeItem(k);
    } catch {
      /* nothing to remove */
    }
  },
};

/**
 * True when this tab must stay out of the shared session. Sticky per tab: once a
 * demo or "Open as" session is created here, the tab stays detached for as long
 * as that session lives, even after navigating away from the demo/open-as path.
 */
export function isDetachedTab(): boolean {
  if (typeof window === "undefined") return true;
  return (
    safeSession.get(DETACHED_KEY) === "1" ||
    safeSession.get(OPEN_AS_SESSION_KEY) !== null ||
    isDetachedPath(window.location.pathname)
  );
}

// ── Transport ────────────────────────────────────────────────────────────────

type Listener = (msg: AuthMessage) => void;
const listeners = new Set<Listener>();
let channel: BroadcastChannel | null = null;
let transportReady = false;

function deliver(data: unknown) {
  const msg = data as AuthMessage | null;
  if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return;
  if (msg.from === TAB_ID) return;
  listeners.forEach((fn) => {
    try {
      fn(msg);
    } catch (e) {
      console.error("authSync listener failed:", e);
    }
  });
}

function ensureTransport() {
  if (transportReady || typeof window === "undefined") return;
  transportReady = true;
  try {
    if (typeof window.BroadcastChannel === "function") {
      channel = new window.BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (e) => deliver(e.data);
      return;
    }
  } catch {
    channel = null;
  }
  // Fallback: a storage event fires in every OTHER tab of this origin.
  window.addEventListener("storage", (e) => {
    if (e.key !== FALLBACK_MSG_KEY || !e.newValue) return;
    try {
      deliver(JSON.parse(e.newValue));
    } catch {
      /* malformed message */
    }
  });
}

function post(msg: AuthMessage) {
  ensureTransport();
  try {
    if (channel) {
      channel.postMessage(msg);
      return;
    }
    window.localStorage.setItem(FALLBACK_MSG_KEY, JSON.stringify(msg));
    window.localStorage.removeItem(FALLBACK_MSG_KEY);
  } catch (e) {
    // Degraded, not broken: the other tabs simply stay as they are.
    console.warn("authSync: could not reach other tabs:", e);
  }
}

function subscribe(fn: Listener): () => void {
  ensureTransport();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Snapshot of this tab's session ───────────────────────────────────────────

function readSnapshot(): SessionSnapshot | null {
  const root = safeSession.get(ROOT_KEY);
  if (!root) return null;
  return { root, csrf: CSRF_KEYS.map(safeSession.get).find(Boolean) ?? null };
}

/** Copy another tab's session into this tab's sessionStorage (before rehydrate). */
export function writeSnapshotToThisTab(snapshot: SessionSnapshot) {
  safeSession.set(ROOT_KEY, snapshot.root);
  if (snapshot.csrf) CSRF_KEYS.forEach((k) => safeSession.set(k, snapshot.csrf as string));
}

// ── navigator.locks (presence + renewal leader) ──────────────────────────────

interface LockManagerLike {
  request: (
    name: string,
    options: { mode?: "shared" | "exclusive"; signal?: AbortSignal },
    cb: () => Promise<void>,
  ) => Promise<unknown>;
  query?: () => Promise<{ held?: { name?: string }[] }>;
}

const getLocks = (): LockManagerLike | null => {
  try {
    const locks = (navigator as unknown as { locks?: LockManagerLike }).locks;
    return locks && typeof locks.request === "function" ? locks : null;
  } catch {
    return null;
  }
};

/**
 * Hold `name` until the returned release() is called. `onGranted` runs once the
 * lock is ours — immediately for a shared lock, possibly much later (when the
 * current holder's tab closes) for an exclusive one.
 */
function holdLock(
  name: string,
  mode: "shared" | "exclusive",
  onGranted?: () => void,
): () => void {
  const locks = getLocks();
  if (!locks) return () => {};
  const abort = new AbortController();
  let release: (() => void) | null = null;
  let cancelled = false;
  locks
    .request(name, { mode, signal: abort.signal }, () => {
      if (cancelled) return Promise.resolve();
      return new Promise<void>((resolve) => {
        release = resolve;
        onGranted?.();
      });
    })
    .catch(() => {
      /* AbortError when released before being granted */
    });
  return () => {
    cancelled = true;
    abort.abort();
    release?.();
  };
}

/** true / false when knowable, null when the browser can't tell us. */
async function anotherTabHoldsSession(): Promise<boolean | null> {
  const locks = getLocks();
  if (!locks?.query) return null;
  try {
    const { held = [] } = await locks.query();
    return held.some((l) => l.name === PRESENCE_LOCK);
  } catch {
    return null;
  }
}

/**
 * Run `start` in exactly one tab at a time (the renewal leader). `start` returns
 * its own stop function. When the leader's tab closes, the next waiting tab
 * takes over. Detached tabs, or browsers without navigator.locks, run it
 * locally — the pre-shared-session behaviour.
 */
export function runAsRenewalLeader(start: () => () => void): () => void {
  if (!isSharedSessionEnabled() || isDetachedTab() || !getLocks()) {
    return start();
  }
  let stop: (() => void) | null = null;
  const releaseLock = holdLock(RENEWAL_LOCK, "exclusive", () => {
    stop = start();
  });
  return () => {
    releaseLock();
    stop?.();
    stop = null;
  };
}

// ── 1. Bootstrap (called from the redux-persist storage adapter) ─────────────

/**
 * Ask the other open tabs for their session. Resolves to null when this tab is
 * detached, nobody is logged in, or nobody answers in time.
 */
export async function requestSessionFromOtherTabs(): Promise<SessionSnapshot | null> {
  if (!isSharedSessionEnabled() || isDetachedTab()) return null;

  const someoneHasSession = await anotherTabHoldsSession();
  // Known-empty browser (the common first visit): don't delay first paint.
  if (someoneHasSession === false) return null;
  const timeoutMs = someoneHasSession ? BOOTSTRAP_TIMEOUT_MS : BOOTSTRAP_BLIND_TIMEOUT_MS;

  const requestId = randomId();
  return new Promise((resolve) => {
    let done = false;
    const finish = (snapshot: SessionSnapshot | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(snapshot);
    };
    // First valid reply wins; later ones (several open tabs) are ignored.
    const unsubscribe = subscribe((msg) => {
      if (msg.type === "SESSION_REPLY" && msg.requestId === requestId && msg.snapshot?.root) {
        finish(msg.snapshot);
      }
    });
    const timer = setTimeout(() => finish(null), timeoutMs);
    post({ type: "SESSION_REQUEST", from: TAB_ID, requestId });
  });
}

// ── 2. Live sync (started once from index.jsx) ───────────────────────────────

interface StoreLike {
  getState: () => any;
  subscribe: (fn: () => void) => () => void;
}

interface PersistorLike {
  getState: () => { bootstrapped?: boolean };
  subscribe: (fn: () => void) => () => void;
  flush: () => Promise<unknown>;
  pause: () => void;
}

export interface AuthSyncOptions {
  store: StoreLike;
  persistor: PersistorLike;
  /** Another tab renewed the token for this same account — adopt it in place. */
  onRemoteTokenRenewed: (token: string) => void;
  /**
   * Another tab logged out — tear down this tab's session, WITHOUT navigating:
   * authSync sends the tab to "/" itself, once the tab is visible.
   */
  onRemoteLogout: () => void;
}

interface Identity {
  token: string | null;
  userId: string | null;
}

const readIdentity = (store: StoreLike): Identity => {
  const auth = store.getState()?.auth;
  const userId = auth?.user?.user_id;
  return {
    token: auth?.token || null,
    userId: userId == null ? null : String(userId),
  };
};

let started = false;

export function startAuthSync({
  store,
  persistor,
  onRemoteTokenRenewed,
  onRemoteLogout,
}: AuthSyncOptions): void {
  if (started || typeof window === "undefined") return;
  started = true;

  // The old build kept a full session — token included — in localStorage seeds,
  // which survive a browser restart. Nothing reads them any more; remove them.
  LEGACY_SEED_KEYS.forEach((k) => {
    try {
      window.localStorage.removeItem(k);
    } catch {
      /* storage unavailable */
    }
  });

  if (!isSharedSessionEnabled()) return;

  let ready = false;
  let last: Identity = { token: null, userId: null };
  /** True while we apply another tab's change, so it is not echoed back. */
  let applyingRemote = false;
  let broadcastTimer: ReturnType<typeof setTimeout> | null = null;
  let releasePresence: (() => void) | null = null;
  let heldForInterview: (() => void) | null = null;
  let heldReload: (() => void) | null = null;

  const syncPresence = (token: string | null) => {
    const shouldHold = !!token && !isDetachedTab();
    if (shouldHold && !releasePresence) {
      releasePresence = holdLock(PRESENCE_LOCK, "shared");
    } else if (!shouldHold && releasePresence) {
      releasePresence();
      releasePresence = null;
    }
  };

  const cancelBroadcast = () => {
    if (broadcastTimer) clearTimeout(broadcastTimer);
    broadcastTimer = null;
  };

  const scheduleSessionBroadcast = () => {
    cancelBroadcast();
    broadcastTimer = setTimeout(async () => {
      broadcastTimer = null;
      try {
        await persistor.flush(); // so the snapshot carries the new token/user
      } catch {
        /* snapshot whatever was written */
      }
      const cur = readIdentity(store);
      const snapshot = readSnapshot();
      if (!cur.token || !snapshot || isDetachedTab()) return;
      post({ type: "SESSION", from: TAB_ID, token: cur.token, userId: cur.userId, snapshot });
    }, SESSION_BROADCAST_DEBOUNCE_MS);
  };

  // Two separate holds; in each, the latest remote change supersedes an older one.
  //  - Mid-interview: hold the WHOLE change (never tear a recording tab down).
  //  - In the background: tear down at once (cheap, and the logged-out token must
  //    not linger), but hold the page reload until the user sees the tab — so ten
  //    open tabs don't all reload at once.
  const runWhenNoInterview = (fn: () => void) => {
    if (!isInterviewTabLockHeld()) {
      fn();
      return;
    }
    heldForInterview = fn;
  };
  const reloadWhenVisible = (fn: () => void) => {
    if (!isTabHidden()) {
      fn();
      return;
    }
    heldReload = fn;
  };
  onInterviewTabLockChange(() => {
    if (!heldForInterview || isInterviewTabLockHeld()) return;
    const fn = heldForInterview;
    heldForInterview = null;
    fn();
  });
  document.addEventListener("visibilitychange", () => {
    if (!heldReload || isTabHidden()) return;
    const fn = heldReload;
    heldReload = null;
    fn();
  });

  const adoptSnapshotAndReload = (snapshot: SessionSnapshot, startFromHome: boolean) => {
    // Stop redux-persist from writing this tab's old state back over the new one.
    try {
      persistor.pause();
    } catch {
      /* ignore */
    }
    writeSnapshotToThisTab(snapshot);
    // A full reload so every guard, socket and cached query starts as the new user.
    // A different account must not reload the previous account's page (an owner's
    // /dashboard/… as a candidate): "/" lets the router send it to its own home.
    if (startFromHome) window.location.replace("/");
    else window.location.reload();
  };

  const handleRemoteSession = (msg: Extract<AuthMessage, { type: "SESSION" }>) => {
    const cur = readIdentity(store);
    if (cur.token === msg.token && cur.userId === msg.userId) return; // already in sync
    if (cur.token && cur.userId && cur.userId === msg.userId) {
      // Same account, new token (renewal in the leader tab): swap it in place.
      applyingRemote = true;
      try {
        onRemoteTokenRenewed(msg.token);
      } finally {
        applyingRemote = false;
      }
      if (msg.snapshot.csrf) CSRF_KEYS.forEach((k) => safeSession.set(k, msg.snapshot.csrf as string));
      last = readIdentity(store);
      // Same account signed in again while a logout was held for the interview.
      heldForInterview = null;
      return;
    }
    // This tab was logged out, or is a different account: become the new session.
    // Nothing to clean up first, so the whole change waits for the tab to be seen.
    // A logged-out tab reloads where it is (/login → its home, a public page stays).
    const switchingAccount = !!cur.token;
    runWhenNoInterview(() =>
      reloadWhenVisible(() => adoptSnapshotAndReload(msg.snapshot, switchingAccount)),
    );
  };

  const handleRemoteLogout = () => {
    if (!readIdentity(store).token) return;
    runWhenNoInterview(() => {
      applyingRemote = true;
      try {
        onRemoteLogout();
      } finally {
        applyingRemote = false;
      }
      // Logged out now; the fresh start on "/" waits until the tab is seen.
      reloadWhenVisible(() => window.location.replace("/"));
    });
  };

  const handleSessionRequest = async (msg: Extract<AuthMessage, { type: "SESSION_REQUEST" }>) => {
    if (!readIdentity(store).token) return;
    try {
      await persistor.flush();
    } catch {
      /* answer with what was written */
    }
    const snapshot = readSnapshot();
    if (!snapshot || isDetachedTab()) return;
    post({ type: "SESSION_REPLY", from: TAB_ID, requestId: msg.requestId, snapshot });
  };

  subscribe((msg) => {
    // Until rehydration finishes this tab's state isn't meaningful; and a
    // detached tab neither answers nor obeys.
    if (!ready || isDetachedTab()) return;
    if (msg.type === "SESSION_REQUEST") void handleSessionRequest(msg);
    else if (msg.type === "SESSION") handleRemoteSession(msg);
    else if (msg.type === "LOGOUT") handleRemoteLogout();
  });

  store.subscribe(() => {
    if (!ready) return;
    const cur = readIdentity(store);
    if (cur.token === last.token && cur.userId === last.userId) return;
    const prev = last;
    last = cur;

    if (isDetachedTab()) {
      // Pin the detachment to the tab while its demo / open-as session lives…
      if (cur.token && isDetachedPath(window.location.pathname)) safeSession.set(DETACHED_KEY, "1");
      // …and lift it once that session is gone and the tab has left the demo.
      if (!cur.token && !isDetachedPath(window.location.pathname)) safeSession.remove(DETACHED_KEY);
      syncPresence(null);
      return;
    }

    syncPresence(cur.token);
    if (applyingRemote) return;

    if (!cur.token) {
      cancelBroadcast();
      // Sent synchronously: signOut() navigates away right after RESET_APP.
      if (prev.token) post({ type: "LOGOUT", from: TAB_ID });
      return;
    }
    scheduleSessionBroadcast();
  });

  // Rehydration (and any bootstrap snapshot it used) is not a local change —
  // start watching from the rehydrated state, so a reload broadcasts nothing.
  const begin = () => {
    ready = true;
    last = readIdentity(store);
    syncPresence(last.token);
  };
  if (persistor.getState().bootstrapped) {
    begin();
  } else {
    const unsubscribe = persistor.subscribe(() => {
      if (!persistor.getState().bootstrapped) return;
      unsubscribe();
      begin();
    });
  }
}
